/**
 * Sales Document Upload Tab Component
 * A drag-and-drop file upload component for sales document processing with AI
 * Based on InvoiceUpload component but adapted for covers volume data
 */

import React, { useCallback, useState, useEffect, useRef } from 'react';
import { useDropzone, type FileRejection } from 'react-dropzone';
import { useLocation } from 'react-router-dom';
import styled from 'styled-components';
import { isAxiosError } from 'axios';
import { Card } from '../ui/Card';
import { Button } from '../ui/Button';
import { ProgressBar } from '../ui/ProgressBar';
import { Icon } from '../ui/Icon';
import { useSalesDocumentOperations } from '../../hooks/useApi';
import { type OcrTaskStartResponse } from '@defs/api';
import {
  useTaskProcessingStore,
  createProcessingTaskFromStartResponse,
} from '../../stores/taskProcessing.store';
import type { ProcessingTask } from '../../stores/taskProcessing.types';
import { TaskStatusList } from '../../features/tasks';

// ============================================================================
// Types & Constants
// ============================================================================

interface FileWithPreview extends File {
  preview: string | undefined;
}

const ACCEPTED_FILE_TYPES = {
  'image/jpeg': ['.jpg', '.jpeg'],
  'image/png': ['.png'],
  'application/pdf': ['.pdf'],
};

const ACCEPTED_MIME_TYPES = Object.keys(ACCEPTED_FILE_TYPES);
const MAX_FILE_SIZE = 5 * 1024 * 1024; // 5MB

function getUploadErrorMessage(error: unknown): string {
  if (
    isAxiosError<{ detail?: unknown }>(error) &&
    typeof error.response?.data?.detail === 'string'
  ) {
    return error.response.data.detail;
  }
  return error instanceof Error
    ? error.message
    : 'Error al procesar el documento. Intenta nuevamente.';
}

// ============================================================================
// Styled Components
// ============================================================================

const UploadContainer = styled.div`
  max-width: 600px;
  margin: 0 auto;
`;

const DropZone = styled.div<{ isDragActive: boolean; hasError: boolean }>`
  border: 1px dashed
    ${({ isDragActive, hasError }) =>
      hasError
        ? 'var(--danger-600)'
        : isDragActive
          ? 'var(--brand-500)'
          : 'var(--border)'};
  border-radius: var(--radius-lg);
  padding: 2rem 1.25rem;
  text-align: center;
  cursor: pointer;
  transition: all 0.3s ease;
  background: ${({ isDragActive, hasError }) =>
    hasError
      ? 'var(--danger-50)'
      : isDragActive
        ? 'var(--brand-50)'
        : 'var(--surface-subtle)'};

  &:hover {
    border-color: var(--brand-400);
    background: var(--brand-50);
  }

  &:focus {
    outline: none;
    box-shadow: 0 0 0 2px var(--brand-200);
  }
`;

const UploadIcon = styled.div`
  width: 80px;
  height: 80px;
  border-radius: 50%;
  background: var(--brand-100);
  display: flex;
  align-items: center;
  justify-content: center;
  margin: 0 auto var(--space-lg);
  font-size: 2rem;
  color: var(--brand-600);
`;

const UploadText = styled.div`
  h3 {
    font-size: var(--font-size-xl);
    font-weight: var(--font-weight-semibold);
    color: var(--text-primary);
    margin-bottom: var(--space-sm);
  }

  p {
    font-size: var(--font-size-base);
    color: var(--text-secondary);
    margin-bottom: var(--space-md);
    line-height: var(--line-height-relaxed);
  }

  .file-types {
    font-size: var(--font-size-sm);
    color: var(--text-muted);
    margin-top: var(--space-md);
  }
`;

const FilePreviewContainer = styled.div`
  display: flex;
  align-items: center;
  gap: var(--space-md);
  padding: var(--space-lg);
  background: var(--surface);
  border: 1px solid var(--border);
  border-radius: var(--radius-md);
  margin: var(--space-lg) 0;
`;

const FileIcon = styled.div`
  width: 48px;
  height: 48px;
  border-radius: var(--radius-sm);
  background: var(--brand-100);
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 1.5rem;
  color: var(--brand-600);
  flex-shrink: 0;
`;

const FileInfo = styled.div`
  flex: 1;
  min-width: 0;

  .file-name {
    font-weight: var(--font-weight-medium);
    color: var(--text-primary);
    margin-bottom: var(--space-xs);
    truncate: true;
  }

  .file-size {
    font-size: var(--font-size-sm);
    color: var(--text-muted);
    margin-bottom: var(--space-xs);
  }

  .file-type {
    font-size: var(--font-size-sm);
    color: var(--text-secondary);
  }
`;

const RemoveButton = styled(Button)`
  padding: var(--space-xs);
  min-width: auto;
`;

const UploadActions = styled.div`
  display: flex;
  gap: var(--space-md);
  justify-content: center;
  margin-top: var(--space-lg);

  @media (max-width: 768px) {
    flex-direction: column;
  }
`;

const ErrorMessage = styled.div`
  background: #fef2f2;
  border: 1px solid #fecaca;
  color: var(--danger-600);
  padding: var(--space-md);
  border-radius: var(--radius-md);
  margin-bottom: var(--space-lg);
  font-size: var(--font-size-sm);
  text-align: center;
`;

const StatusList = styled(TaskStatusList)`
  margin-top: var(--space-xl);
`;

// ============================================================================
// Component
// ============================================================================

export const SalesDocumentUploadTab: React.FC = () => {
  const location = useLocation();
  const { scanSalesDocument, isLoading: isMutationLoading } =
    useSalesDocumentOperations();
  const startTask = useTaskProcessingStore((state) => state.startTask);
  const salesTaskMatch = useCallback(
    (task: ProcessingTask) =>
      task.type === 'ocr' && task.subtype?.startsWith('sales'),
    [],
  );
  const [currentFile, setCurrentFile] = useState<FileWithPreview | null>(null);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [error, setError] = useState<string | null>(null);
  const isMountedRef = useRef(true);
  const latestPathRef = useRef(location.pathname);

  useEffect(() => {
    latestPathRef.current = location.pathname;
  }, [location.pathname]);

  useEffect(() => {
    isMountedRef.current = true;
    return () => {
      isMountedRef.current = false;
    };
  }, []);

  // File validation
  const validateFile = useCallback((file: File): string | null => {
    // Check file type
    if (!ACCEPTED_MIME_TYPES.includes(file.type)) {
      return 'Tipo de archivo no válido. Solo se aceptan JPG, PNG y PDF.';
    }

    // Check file size
    if (file.size > MAX_FILE_SIZE) {
      return 'El archivo es demasiado grande. El tamaño máximo es 5MB.';
    }

    return null;
  }, []);

  // File drop handler
  const onDrop = useCallback(
    (acceptedFiles: File[], rejectedFiles: FileRejection[]) => {
      setError(null);

      if (rejectedFiles.length > 0) {
        const rejection = rejectedFiles[0];
        if (!rejection) {
          setError('Archivo no válido.');
          return;
        }
        if (rejection.errors.some((error) => error.code === 'file-too-large')) {
          setError('El archivo es demasiado grande. El tamaño máximo es 5MB.');
        } else if (
          rejection.errors.some((error) => error.code === 'file-invalid-type')
        ) {
          setError(
            'Tipo de archivo no válido. Solo se aceptan JPG, PNG y PDF.',
          );
        } else {
          setError('Archivo no válido.');
        }
        return;
      }

      if (acceptedFiles.length === 0) {
        setError('No se seleccionó ningún archivo válido.');
        return;
      }

      const firstFile = acceptedFiles[0];

      if (!firstFile || !(firstFile instanceof File)) {
        setError('No se seleccionó ningún archivo válido.');
        return;
      }

      const validationError = validateFile(firstFile);

      if (validationError) {
        setError(validationError);
        return;
      }

      // Create file with preview
      const fileWithPreview: FileWithPreview = Object.assign(firstFile, {
        preview: firstFile.type.startsWith('image/')
          ? URL.createObjectURL(firstFile)
          : undefined,
      });

      setCurrentFile(fileWithPreview);
      setUploadProgress(0);
    },
    [validateFile],
  );

  // Cleanup file preview URL
  const removeFile = useCallback(() => {
    if (currentFile?.preview) {
      URL.revokeObjectURL(currentFile.preview);
    }
    setCurrentFile(null);
    setError(null);
    setUploadProgress(0);
  }, [currentFile]);

  // Upload file handler
  const uploadFile = useCallback(async () => {
    if (!currentFile) return;

    if (!isMountedRef.current) {
      return;
    }

    setUploadProgress(0);
    setError(null);

    try {
      const formData = new FormData();
      formData.append('file', currentFile);

      setUploadProgress(35);

      const response: OcrTaskStartResponse = await scanSalesDocument(formData);

      if (!response?.taskId) {
        throw new Error(
          'Respuesta inválida del servidor - ID de tarea faltante',
        );
      }

      const task = createProcessingTaskFromStartResponse(
        response,
        currentFile.name,
      );
      startTask(task);

      setUploadProgress(100);
      removeFile();
    } catch (error: unknown) {
      if (isMountedRef.current) {
        setUploadProgress(0);
      }
      setError(getUploadErrorMessage(error));
    }
  }, [currentFile, scanSalesDocument, removeFile, startTask]);

  // Dropzone configuration
  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    accept: ACCEPTED_FILE_TYPES,
    maxSize: MAX_FILE_SIZE,
    multiple: false,
    disabled: isMutationLoading,
  });

  // Helper functions
  const formatFileSize = (bytes: number): string => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return `${parseFloat((bytes / Math.pow(k, i)).toFixed(2))} ${sizes[i]}`;
  };

  const getFileIcon = (_file: File) => (
    <Icon name="file" size="lg" aria-hidden />
  );

  const getFileTypeName = (file: File): string => {
    if (file.type === 'application/pdf') return 'PDF';
    if (file.type === 'image/jpeg') return 'JPEG';
    if (file.type === 'image/png') return 'PNG';
    return file.type;
  };

  return (
    <Card>
      <UploadContainer>
        {error && <ErrorMessage>{error}</ErrorMessage>}

        <DropZone
          {...getRootProps()}
          isDragActive={isDragActive}
          hasError={!!error}
        >
          <input {...getInputProps()} />
          <UploadIcon>
            <Icon name="file-upload" size="lg" aria-hidden />
          </UploadIcon>
          <UploadText>
            <h3>
              {isDragActive
                ? '¡Suelta el documento aquí!'
                : 'Sube tu reporte de ventas'}
            </h3>
            <p>
              Arrastra y suelta tu documento de reporte de restaurante aquí, o{' '}
              <strong>haz clic para seleccionar</strong>
            </p>
            <div className="file-types">
              Formatos soportados: PDF, JPG, PNG • Tamaño máximo: 5MB
            </div>
          </UploadText>
        </DropZone>

        {/* File preview */}
        {currentFile && (
          <FilePreviewContainer>
            <FileIcon>{getFileIcon(currentFile)}</FileIcon>
            <FileInfo>
              <div className="file-name">{currentFile.name}</div>
              <div className="file-size">
                {formatFileSize(currentFile.size)}
              </div>
              <div className="file-type">{getFileTypeName(currentFile)}</div>
            </FileInfo>
            <RemoveButton
              variant="tertiary"
              onClick={removeFile}
              disabled={isMutationLoading}
              aria-label="Remover archivo"
            >
              <Icon name="close" size="xs" aria-hidden />
            </RemoveButton>
          </FilePreviewContainer>
        )}

        {/* Upload progress */}
        {isMutationLoading && (
          <ProgressBar
            progress={uploadProgress}
            text="Procesando documento de ventas..."
            variant="primary"
            size="md"
          />
        )}

        {/* Upload actions */}
        {currentFile && !isMutationLoading && (
          <UploadActions>
            <Button onClick={uploadFile} fullWidth>
              Procesar documento
            </Button>
          </UploadActions>
        )}

        <StatusList matchTask={salesTaskMatch} />
      </UploadContainer>
    </Card>
  );
};

SalesDocumentUploadTab.displayName = 'SalesDocumentUploadTab';
